import React from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardList,
  Pause,
  Play,
  Printer,
  RotateCcw,
  Scale,
  Timer,
} from "lucide-react";
import {
  blendRecipes,
  buildProcessSteps,
  calculateBatch,
  paperModes,
  sheetSizes,
} from "./data/blends";

const STORAGE_KEY = "hydro-terrace-blend-runbook-v1";

function loadSavedRun() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function makeBatchId() {
  const date = new Date();
  const stamp = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("");
  return `HT-${stamp}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

function formatTimer(seconds) {
  const value = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const remainingSeconds = value % 60;
  if (hours > 0) return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

function formatWater(milliliters) {
  if (milliliters >= 1000) return `${(milliliters / 1000).toFixed(2)} L`;
  return `${Math.round(milliliters)} mL`;
}

export default function BlendRunbook() {
  const saved = React.useMemo(loadSavedRun, []);
  const [blendId, setBlendId] = React.useState(saved.blendId || blendRecipes[0].id);
  const [sizeId, setSizeId] = React.useState(saved.sizeId || "medium");
  const [sheetCount, setSheetCount] = React.useState(saved.sheetCount || 10);
  const [paperModeId, setPaperModeId] = React.useState(saved.paperModeId || "control");
  const [operator, setOperator] = React.useState(saved.operator || "");
  const [batchId, setBatchId] = React.useState(saved.batchId || makeBatchId());
  const [paperLot, setPaperLot] = React.useState(saved.paperLot || "");
  const [packagingLot, setPackagingLot] = React.useState(saved.packagingLot || "");
  const [reviewer, setReviewer] = React.useState(saved.reviewer || "");
  const [seedLots, setSeedLots] = React.useState(saved.seedLots || {});
  const [notes, setNotes] = React.useState(saved.notes || "");
  const [completed, setCompleted] = React.useState(saved.completed || []);
  const [activeStepId, setActiveStepId] = React.useState(saved.activeStepId || "intake");
  const [remainingSeconds, setRemainingSeconds] = React.useState(saved.remainingSeconds || 0);
  const [timerEndAt, setTimerEndAt] = React.useState(saved.timerEndAt || null);
  const [timerRunning, setTimerRunning] = React.useState(Boolean(saved.timerEndAt && saved.timerEndAt > Date.now()));

  const blend = blendRecipes.find((item) => item.id === blendId) || blendRecipes[0];
  const batch = React.useMemo(
    () => calculateBatch({ blend, size: sizeId, sheets: sheetCount, paperMode: paperModeId }),
    [blend, sizeId, sheetCount, paperModeId],
  );
  const steps = React.useMemo(() => buildProcessSteps({ blend, batch }), [blend, batch]);
  const activeIndex = Math.max(0, steps.findIndex((step) => step.id === activeStepId));
  const activeStep = steps[activeIndex];
  const formulaKey = `${blendId}:${sizeId}:${sheetCount}:${paperModeId}`;
  const previousFormulaRef = React.useRef(formulaKey);
  const previousStepRef = React.useRef(activeStepId);

  React.useEffect(() => {
    if (previousFormulaRef.current === formulaKey) return;
    previousFormulaRef.current = formulaKey;
    setActiveStepId("intake");
    setCompleted([]);
    setTimerRunning(false);
    setTimerEndAt(null);
    setRemainingSeconds(steps[0]?.durationSeconds || 0);
  }, [formulaKey, steps]);

  React.useEffect(() => {
    if (!activeStep) return;
    if (previousStepRef.current !== activeStep.id) {
      previousStepRef.current = activeStep.id;
      setTimerRunning(false);
      setTimerEndAt(null);
      setRemainingSeconds(activeStep.durationSeconds);
      return;
    }
    if (!timerRunning && !timerEndAt && remainingSeconds === 0) {
      setRemainingSeconds(activeStep.durationSeconds);
    }
  }, [activeStep, remainingSeconds, timerEndAt, timerRunning]);

  React.useEffect(() => {
    if (!timerRunning || !timerEndAt) return undefined;
    const tick = () => {
      const next = Math.max(0, Math.ceil((timerEndAt - Date.now()) / 1000));
      setRemainingSeconds(next);
      if (next === 0) {
        setTimerRunning(false);
        setTimerEndAt(null);
      }
    };
    tick();
    const interval = setInterval(tick, 250);
    return () => clearInterval(interval);
  }, [timerEndAt, timerRunning]);

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      blendId,
      sizeId,
      sheetCount,
      paperModeId,
      operator,
      batchId,
      paperLot,
      packagingLot,
      reviewer,
      seedLots,
      notes,
      completed,
      activeStepId,
      remainingSeconds,
      timerEndAt,
    }));
  }, [
    activeStepId,
    batchId,
    blendId,
    completed,
    notes,
    operator,
    packagingLot,
    paperLot,
    paperModeId,
    remainingSeconds,
    reviewer,
    seedLots,
    sheetCount,
    sizeId,
    timerEndAt,
  ]);

  function startTimer() {
    const seconds = remainingSeconds > 0 ? remainingSeconds : activeStep.durationSeconds;
    const endAt = Date.now() + seconds * 1000;
    setRemainingSeconds(seconds);
    setTimerEndAt(endAt);
    setTimerRunning(true);
  }

  function pauseTimer() {
    if (timerEndAt) setRemainingSeconds(Math.max(0, Math.ceil((timerEndAt - Date.now()) / 1000)));
    setTimerRunning(false);
    setTimerEndAt(null);
  }

  function resetTimer() {
    setTimerRunning(false);
    setTimerEndAt(null);
    setRemainingSeconds(activeStep.durationSeconds);
  }

  function selectStep(index) {
    const next = steps[Math.max(0, Math.min(index, steps.length - 1))];
    if (next) setActiveStepId(next.id);
  }

  function markComplete() {
    setCompleted((current) => current.includes(activeStep.id) ? current : [...current, activeStep.id]);
    if (activeIndex < steps.length - 1) selectStep(activeIndex + 1);
  }

  function newBatch() {
    setBatchId(makeBatchId());
    setOperator("");
    setPaperLot("");
    setPackagingLot("");
    setReviewer("");
    setSeedLots({});
    setNotes("");
    setCompleted([]);
    setActiveStepId("intake");
    setTimerRunning(false);
    setTimerEndAt(null);
    setRemainingSeconds(steps[0]?.durationSeconds || 0);
  }

  const completionPercent = Math.round((completed.length / steps.length) * 100);

  return (
    <section className="blendRunbook" id="blend-runbook">
      <header className="runbookHeader">
        <div>
          <span className="eyebrow">Outsource-ready validation SOP</span>
          <h2>Interactive blend workshop</h2>
          <p>Choose the batch, weigh the calculated formula, then run each timed step and release gate in order.</p>
        </div>
        <div className="runbookHeaderActions">
          <button className="iconTextButton" type="button" onClick={() => window.print()}><Printer size={16} /> Print run</button>
          <button className="iconTextButton" type="button" onClick={newBatch}><RotateCcw size={16} /> New batch</button>
        </div>
      </header>

      <div className="runbookConfigurator">
        <label>
          <span>Blend</span>
          <select value={blendId} onChange={(event) => setBlendId(event.target.value)}>
            {blendRecipes.map((recipe) => <option key={recipe.id} value={recipe.id}>{recipe.name}</option>)}
          </select>
        </label>
        <fieldset>
          <legend>Sheet size</legend>
          <div className="segmentedControl">
            {Object.entries(sheetSizes).map(([id, size]) => (
              <button key={id} type="button" className={sizeId === id ? "active" : ""} onClick={() => setSizeId(id)}>{size.name}</button>
            ))}
          </div>
        </fieldset>
        <label>
          <span>Number of sheets</span>
          <input type="number" min="1" max="500" value={sheetCount} onChange={(event) => setSheetCount(Math.max(1, Math.min(500, Number(event.target.value) || 1)))} />
        </label>
        <label>
          <span>Paper mode</span>
          <select value={paperModeId} onChange={(event) => setPaperModeId(event.target.value)}>
            {Object.entries(paperModes).map(([id, mode]) => <option key={id} value={id}>{mode.name}</option>)}
          </select>
        </label>
      </div>

      <div className="recipeIdentity">
        <div>
          <span className={`runbookStatus ${blend.availability.includes("Future") ? "future" : "current"}`}>{blend.availability}</span>
          <h3>{blend.name}</h3>
          <p>{blend.color} | {blend.flavor}</p>
        </div>
        <dl>
          <div><dt>Harvest</dt><dd>{blend.harvest}</dd></div>
          <div><dt>Format</dt><dd>{blend.sizePolicy}</dd></div>
          <div><dt>Dose</dt><dd>{batch.seedPerSheet.toFixed(2)} g seed/sheet</dd></div>
          <div><dt>Paper</dt><dd>{batch.sheet.dryPaperMass.toFixed(2)} g dry/sheet</dd></div>
        </dl>
      </div>

      <div className="runbookMetrics">
        <div><Scale size={18} /><span>{batch.totalSeed.toFixed(2)} g</span><label>Total seed</label></div>
        <div><ClipboardList size={18} /><span>{batch.totalPaper.toFixed(2)} g</span><label>Total dry paper</label></div>
        <div><Timer size={18} /><span>{formatWater(batch.processWaterMl)}</span><label>Starting process water</label></div>
        <div><Check size={18} /><span>{completionPercent}%</span><label>Run complete</label></div>
      </div>

      <div className="runbookWorkspace">
        <aside className="batchFormula">
          <div className="batchRecordHeader">
            <div>
              <span className="eyebrow">Batch record</span>
              <strong>{batchId}</strong>
            </div>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
          <div className="batchRecordFields">
            <label><span>Operator</span><input value={operator} onChange={(event) => setOperator(event.target.value)} placeholder="Full name" /></label>
            <label><span>Batch ID</span><input value={batchId} onChange={(event) => setBatchId(event.target.value)} /></label>
            <label><span>Pulp / fiber lot</span><input value={paperLot} onChange={(event) => setPaperLot(event.target.value)} placeholder="Lot IDs" /></label>
            <label><span>Packaging lot</span><input value={packagingLot} onChange={(event) => setPackagingLot(event.target.value)} placeholder="Pouch / desiccant lots" /></label>
            <label className="wideField"><span>Release reviewer</span><input value={reviewer} onChange={(event) => setReviewer(event.target.value)} placeholder="Complete after grow-out" /></label>
          </div>

          <section className="ingredientSection">
            <header><h4>Seed blend</h4><span>Percent by weight</span></header>
            <div className="ingredientTable" role="table" aria-label="Seed blend weights">
              <div className="ingredientRow ingredientHead" role="row"><span>Seed</span><span>%</span><span>Per sheet</span><span>Batch</span><span>Lot</span></div>
              {batch.seedIngredients.map((item) => (
                <div className="ingredientRow" role="row" key={item.id}>
                  <strong>{item.name}<small>{item.purchaseStatus}</small></strong>
                  <span>{item.percent}%</span>
                  <span>{(item.grams / batch.count).toFixed(2)} g</span>
                  <span>{item.grams.toFixed(2)} g</span>
                  <input aria-label={`${item.name} lot`} value={seedLots[item.id] || ""} onChange={(event) => setSeedLots({ ...seedLots, [item.id]: event.target.value })} placeholder="Lot" />
                </div>
              ))}
            </div>
          </section>

          <section className="ingredientSection">
            <header><h4>Paper blend</h4><span>{batch.paper.name}</span></header>
            <div className="paperIngredientList">
              {batch.paperIngredients.map((item) => (
                <div key={item.id}><strong>{item.name}</strong><span>{item.percent.toFixed(2)}%</span><b>{item.grams.toFixed(2)} g</b></div>
              ))}
              <div><strong>Potable process water</strong><span>Starting volume</span><b>{formatWater(batch.processWaterMl)}</b></div>
            </div>
            <p className="formulaNote">{batch.paper.note} Water is a controlled process parameter, not part of final dry mass.</p>
          </section>

          <label className="batchNotes">
            <span>Actual readings, deviations, and notes</span>
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Record actual weights, pH/EC, drying readings, observations, and disposition." />
          </label>
        </aside>

        <div className="stepRunner">
          <div className="stepProgress">
            <div><span>{completed.length} of {steps.length} complete</span><strong>{completionPercent}%</strong></div>
            <div className="progressTrack"><span style={{ width: `${completionPercent}%` }} /></div>
          </div>

          <nav className="stepRail" aria-label="Blend-making steps">
            {steps.map((step, index) => (
              <button key={step.id} type="button" className={`${step.id === activeStep.id ? "active" : ""} ${completed.includes(step.id) ? "done" : ""}`} onClick={() => selectStep(index)}>
                {completed.includes(step.id) ? <Check size={15} /> : <Circle size={14} />}
                <span><small>{step.phase}</small>{index + 1}. {step.title}</span>
              </button>
            ))}
          </nav>

          <article className="activeStepCard">
            <header>
              <div>
                <span className="eyebrow">{activeStep.phase} | Step {activeIndex + 1} of {steps.length}</span>
                <h3>{activeStep.title}</h3>
              </div>
              <div className={`stepTimer ${timerRunning ? "running" : ""}`}>
                <span>{formatTimer(remainingSeconds)}</span>
                <small>Target {formatTimer(activeStep.durationSeconds)}</small>
              </div>
            </header>

            <div className="timerControls">
              {timerRunning ? (
                <button type="button" onClick={pauseTimer}><Pause size={16} /> Pause</button>
              ) : (
                <button type="button" className="primaryTimer" onClick={startTimer}><Play size={16} /> Start timer</button>
              )}
              <button type="button" onClick={resetTimer}><RotateCcw size={15} /> Reset</button>
            </div>

            <ol className="instructionList">
              {activeStep.instructions.map((instruction) => <li key={instruction}>{instruction}</li>)}
            </ol>

            <div className="releaseGate">
              <strong>Do not continue until</strong>
              <p>{activeStep.gate}</p>
            </div>

            <footer>
              <button type="button" onClick={() => selectStep(activeIndex - 1)} disabled={activeIndex === 0}><ChevronLeft size={16} /> Previous</button>
              <button type="button" className="completeStep" onClick={markComplete}><Check size={16} /> Complete step</button>
              <button type="button" onClick={() => selectStep(activeIndex + 1)} disabled={activeIndex === steps.length - 1}>Next <ChevronRight size={16} /></button>
            </footer>
          </article>
        </div>
      </div>

      <details className="runbookBasis">
        <summary>Method basis and limits</summary>
        <p>This is a controlled validation SOP, not proof that a blend is ready for sale. Percentages are seed weight proportions. The total dose scales from the blend's weighted 10x20 tray rate and the selected HydroTerrace seeded area. The paper control uses 91.5% base pulp, 6.5% hemp/coco microfiber, and 2% seed-paper moisture pulp.</p>
        <p>Method references: University of Maryland Extension microgreen guidance, Arnold Grummer seed-paper guidance, and University of Nevada, Reno microgreen produce-safety guidance. Supplier contact times and sanitizer labels always override the generic timer.</p>
        <div>
          <a href="https://extension.umd.edu/resource/growing-microgreens-and-baby-greens-indoors/" target="_blank" rel="noreferrer">UMD microgreen guidance</a>
          <a href="https://arnoldgrummer.com/faqs-seed-paper-projects.html" target="_blank" rel="noreferrer">Arnold Grummer seed-paper guidance</a>
          <a href="https://extension.unr.edu/publication.aspx?PubID=3468" target="_blank" rel="noreferrer">UNR microgreen safety</a>
        </div>
      </details>
    </section>
  );
}
